import React, { useCallback, useMemo } from 'react';
import { InlineLoading, Modal } from '@carbon/react';
import { useAtom } from 'jotai';
import { useLatest } from 'lib/hooks/use-latest';
import type { ReconcileCreateResponse } from 'types/api-responses';
import type { ResolutionChangeReason } from 'types/api-payloads';
import {
  reconcileCreateAtom,
  reconcileCreateUrl,
  reconcilePreviewAtom,
  reconcilePreviewUrl,
} from '../atoms/reconcile';
import { CLIENT_SESSION_ID } from '../atoms/structured-parties';
import { sharedAnchorMessage } from '../utils';
import type { SharedWith } from '../types';
import { actionErrorMessage } from 'utils/api-errors';
import { ENTITY_TYPE_LABEL, type ReconcileCandidate } from './types';
import { useMatchReviewReconcile } from './hooks';
import {
  aliasGroupExtrasFor,
  consolidationCandidate,
} from 'pages/entities/show/reconcile/reconcile-candidate-helpers';
import { NoReconcileCandidatesModal } from 'pages/entities/show/reconcile/no-reconcile-candidates-modal';
import { CANDIDATES_NOT_ASSEMBLED_MESSAGE } from 'pages/entities/show/reconcile/utils';
import ConsolidationTearsheet, {
  type ConsolidationTearsheetProps,
} from 'pages/entities/show/reconcile/consolidation-tearsheet';

interface Props {
  open: boolean;
  onClose: () => void;
  documentId: number;
  // structured_party_id (the staged ImportedEntity wrapper) — scopes the undo stack.
  partyId: number;
  // The party's current anchor, whose live candidates the tearsheet lists.
  anchorId: number | null;
  sessionId: string;
  partyLabel?: string;
  // The party's persisted rows (anchor first, then its suggestions): the anchor
  // row, the §3.2 pre-check and any user rows the live list lacks come from here.
  candidates: ReconcileCandidate[];
  sharedWith?: SharedWith;
  onComplete: (result: ReconcileCreateResponse) => void;
  // The server refused the reconcile because the party's resolution changed
  // since the open: the session is stale. `reason` says whether the party was
  // resolved or moved to another anchor.
  onResolutionChanged: (reason?: ResolutionChangeReason) => void;
  onCanonized?: () => void;
}

// Match Review (doc-structuring) reconcile surface. Each open loads the live
// scored candidates for the party's anchor as of the open (the endpoint the
// entity page uses) and holds them for the session, so a party-detail refetch
// never resets the examiner's consolidation. Renders the shared
// ConsolidationTearsheet with the structured-parties endpoints.
const MatchReviewConsolidationTearsheet: React.FC<Props> = ({
  open,
  onClose,
  documentId,
  partyId,
  anchorId,
  sessionId,
  partyLabel,
  candidates,
  sharedWith,
  onComplete,
  onResolutionChanged,
  onCanonized,
}) => {
  const load = useMatchReviewReconcile(open, anchorId, candidates);
  const session = load.status === 'ready' ? load.session : null;

  const aliasGroupExtras = useMemo(
    () =>
      aliasGroupExtrasFor(
        session?.live ?? [],
        session
          ? {
              id: session.anchorId,
              aliasGroup: session.response.viewedAliasGroup,
            }
          : undefined
      ),
    [session]
  );
  const toModel = useCallback(
    (c: ReconcileCandidate) => consolidationCandidate(c, aliasGroupExtras),
    [aliasGroupExtras]
  );

  const resolutionChanged = useLatest(onResolutionChanged);
  const [previewState, triggerPreview] = useAtom(reconcilePreviewAtom);
  const [createState, triggerCreate] = useAtom(reconcileCreateAtom);

  // Match Review preview sends only entity_ids (the response supplies just the
  // load-time fingerprints); the grouping is client-side.
  const submitPreview = useCallback<
    ConsolidationTearsheetProps['submitPreview']
  >(
    (ids, _groups, onSuccess, onError) =>
      triggerPreview({
        url: reconcilePreviewUrl(documentId, partyId),
        type: 'POST',
        data: { entity_ids: ids },
        onSuccess: (data) => onSuccess(data),
        onError: (err) =>
          onError(actionErrorMessage(err, 'Could not preview reconciliation')),
      }),
    [triggerPreview, documentId, partyId]
  );

  // The create names the anchor the session opened on, which the server
  // refuses once the party has moved off it.
  const submitCreate = useCallback<
    ConsolidationTearsheetProps<ReconcileCreateResponse>['submitCreate']
  >(
    (data, onSuccess, onError) =>
      triggerCreate({
        url: reconcileCreateUrl(documentId, partyId),
        type: 'POST',
        data: {
          ...data,
          client_session_id: CLIENT_SESSION_ID,
          anchor_entity_id: session?.anchorId,
        },
        onSuccess,
        onError: (err) =>
          err?.resolutionChanged
            ? resolutionChanged.current(err.reason)
            : onError(err),
      }),
    [triggerCreate, documentId, partyId, session?.anchorId, resolutionChanged]
  );

  if (!open) return null;

  const title = partyLabel
    ? `Reconcile Entities — ${partyLabel}`
    : 'Reconcile Entities';

  if (load.status === 'loading') {
    return (
      <Modal open passiveModal modalHeading={title} onRequestClose={onClose}>
        <InlineLoading description="Loading reconcile candidates" />
      </Modal>
    );
  }

  if (load.status === 'empty') {
    return <NoReconcileCandidatesModal open onClose={onClose} />;
  }

  if (load.status === 'no-anchor') {
    return (
      <Modal
        open
        modalHeading="Nothing to reconcile"
        primaryButtonText="OK"
        onRequestClose={onClose}
        onRequestSubmit={onClose}
      >
        <p>This party has no anchor entity.</p>
      </Modal>
    );
  }

  if (load.status === 'failed') {
    return (
      <Modal
        open
        modalHeading="Could not load candidates"
        primaryButtonText="OK"
        onRequestClose={onClose}
        onRequestSubmit={onClose}
      >
        <p>{CANDIDATES_NOT_ASSEMBLED_MESSAGE}</p>
      </Modal>
    );
  }

  if (!session) return null;

  return (
    <ConsolidationTearsheet<ReconcileCreateResponse>
      open
      onClose={onClose}
      sessionId={sessionId}
      title={title}
      originalCandidates={session.rows}
      toModel={toModel}
      preCheckIds={session.preCheckIds}
      moreFound={!!session.response.moreFound}
      totalBeforeCap={session.response.totalBeforeCap ?? session.live.length}
      shownCount={session.live.length}
      primaryEntityId={session.anchorId}
      primaryEntityType={ENTITY_TYPE_LABEL[session.anchorType]}
      previewLoading={previewState.loading}
      createLoading={createState.loading}
      submitPreview={submitPreview}
      submitCreate={submitCreate}
      onComplete={(result) => onComplete(result)}
      onCanonized={onCanonized}
      whatReconcileWillDoNote={sharedAnchorMessage(sharedWith)}
    />
  );
};

export default MatchReviewConsolidationTearsheet;
